import React, { useEffect, useRef, useState } from 'react';
import { Router, matchPath, useLocation } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import mixpanel from 'mixpanel-browser';
import { ReflagProvider } from '@reflag/react-sdk';

import { history, store } from './redux/store';
import axiosUtil from './lib/axios';
import configure from './constants/config';
import { useCurrentUserInfo } from 'hooks/data/user/useCurrentUserInfo';
import { CommonRoutes, CustomRoutes } from './routes';
import {
  AUTH_GATE_COPY_PROFILE,
  AUTH_GATE_CHOOSE_ACCOUNT,
} from './routes/CommonRoutes';
import { initLogrocket, initSentry, identifySentry } from 'lib/tracking';
import 'layouts/legacyAssets';
import 'helpers/pdf/fonts';
import ContentLoader from './components/ContentLoader';
import DownloadTrayContainer from './components/DownloadTrayContainer';
import useUser from 'hooks/ui/useUser';
import WhiteLabellingProvider, {
  useWhiteLabellingContext,
} from 'contexts/WhiteLabellingContext';
import PageError from 'views/PageError';
import { useVersionMismatchChecker } from 'hooks/data/useVersionMismatchChecker';
import { minutesToMilliseconds } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { useForcedRefresh, useDisclosure } from 'further-ui/hooks';
import { useAuthClaims } from 'hooks/ui/useAuthClaims';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { Realm } from 'further-types/global';
import getUserType from 'utils/getUserType';
import SwitchAccountModal from 'components/SwitchAccountModal';
import { useResolveFirmAccount } from 'hooks/data/user/useResolveFirmAccount';
import { useRefreshGreenInvestorAccounts } from 'hooks/data/user/useRefreshGreenInvestorAccounts';

const PortalLayout = React.lazy(() => import('./layouts/Portal'));
const RawLayout = React.lazy(() => import('./layouts/Raw'));
const LpPortalLayout = React.lazy(() => import('./layouts/LpPortal'));

axiosUtil.initalise(store);

function AppContent() {
  const location = useLocation();
  const { pathname } = location;

  const isCustomRoute = CustomRoutes.some(({ path, exact }) =>
    matchPath(location?.pathname, { path, exact }),
  );
  const isLpRoute = CommonRoutes.some(
    ({ path, exact, restrictToInvestorRealm, useLpPortalLayout }) =>
      matchPath(location?.pathname, { path, exact }) &&
      (restrictToInvestorRealm === Realm.Lp || useLpPortalLayout),
  );

  const user = useUser();
  const { userIsEisInvestor, userIsLpInvestor, userIsAdviser, userIsOrg } =
    getUserType(user);

  const routesWithoutDownloadTray = [
    AUTH_GATE_COPY_PROFILE,
    AUTH_GATE_CHOOSE_ACCOUNT,
  ];
  const isRouteWithoutDownloadTray = routesWithoutDownloadTray.some((route) =>
    matchPath(location?.pathname, { path: route, exact: true }),
  );

  const {
    isLoading,
    isSubdomain,
    status,
    updatePendingAccount,
    linkedFirmIds,
  } = useWhiteLabellingContext();

  const isLoggedIn = user?.isAuthenticated && !user?.authGateDecision;
  const isAdviserOrOrg = userIsAdviser || userIsOrg;

  const isVisitingPrivateRoute = CommonRoutes.some(
    ({ path, exact, private: isPrivate }) =>
      !!isPrivate && !!matchPath(location?.pathname, { path, exact }),
  );

  // Hydrate from the cookie whenever not logged in (root or subdomain),
  // so arriving on the root host also picks up an existing session.
  const shouldBootstrapAuth = !isLoggedIn;
  const showBootstrapLoader = shouldBootstrapAuth && isVisitingPrivateRoute;

  const currentUserInfo = useCurrentUserInfo({
    alwaysEnabled: shouldBootstrapAuth,
  });
  const previousPathname = useRef(pathname);
  const versionMismatch = useVersionMismatchChecker({
    enabled: process.env.REACT_APP_STAGE === 'production',
    refetchTime: minutesToMilliseconds(4),
  });
  useForcedRefresh({
    enabled: versionMismatch && previousPathname.current !== pathname,
    retryTimeMs: minutesToMilliseconds(5),
  });
  const {
    isOpen: isSwitchAccountModalOpen,
    onOpen: onSwitchAccountModalOpen,
    onClose: onSwitchAccountModalClose,
  } = useDisclosure();

  const [isWaitingForAccountSwitch, setIsWaitingForAccountSwitch] =
    useState(false);

  const urlParams = new URLSearchParams(location.search);
  const targetAccountId = urlParams.get('accountId');
  const targetUserType = urlParams.get('userType');

  const { isResolving: isResolvingAccountContext } = useResolveFirmAccount({
    isSubdomain,
    pathname,
    isLoggedIn,
    userType: user.userType,
    activeAccountIds: user.activeAccountIds,
    firmIds: linkedFirmIds,
    refetchCurrentUserInfo: currentUserInfo.refetch,
  });

  const { isRefreshing: isRefreshingGreenInvestorAccounts } =
    useRefreshGreenInvestorAccounts({
      isSubdomain,
      isLoggedIn,
      userType: user.userType,
      activeAccountIds: user.activeAccountIds,
      refetchCurrentUserInfo: currentUserInfo.refetch,
    });

  useEffect(() => {
    if (!targetAccountId || !targetUserType) {
      setIsWaitingForAccountSwitch(false);
      return;
    }

    if (!isLoggedIn) {
      updatePendingAccount({
        accountId: targetAccountId,
        userType: parseInt(targetUserType),
      });
      setIsWaitingForAccountSwitch(false);
    } else if (
      user.activeAccountIds &&
      !user.activeAccountIds.includes(targetAccountId)
    ) {
      setIsWaitingForAccountSwitch(true);
      onSwitchAccountModalOpen();
    } else {
      setIsWaitingForAccountSwitch(false);
    }
  }, [targetAccountId, targetUserType, isLoggedIn, user.activeAccountIds]);

  const routes = CommonRoutes.filter((rec) =>
    isSubdomain ? !rec.hideForSubDomains : !rec.onlyAllowedInSubDomain,
  ).filter((rec) => {
    if (
      user?.isAuthenticated &&
      rec.restrictToInvestorRealm === Realm.Eis &&
      !userIsEisInvestor
    ) {
      return false;
    }
    if (
      user?.isAuthenticated &&
      rec.restrictToInvestorRealm === Realm.Lp &&
      !userIsLpInvestor
    ) {
      return false;
    }
    return true;
  });

  const Layout = isLpRoute
    ? LpPortalLayout
    : isCustomRoute
    ? RawLayout
    : PortalLayout;

  if (status === 'error' && isSubdomain) {
    return <PageError />;
  }

  if (
    isResolvingAccountContext ||
    isRefreshingGreenInvestorAccounts ||
    (showBootstrapLoader && currentUserInfo.isFetching)
  ) {
    return <ContentLoader isLp={isLpRoute} />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <React.Suspense fallback={<ContentLoader isLp={isLpRoute} />}>
        {isLoading ? (
          <ContentLoader isLp={isLpRoute} />
        ) : (
          <>
            {!isWaitingForAccountSwitch && <Layout routes={routes} />}
            {isSwitchAccountModalOpen &&
              targetAccountId &&
              targetUserType !== null && (
                <SwitchAccountModal
                  accountId={targetAccountId}
                  userType={parseInt(targetUserType)}
                  isOpen={isSwitchAccountModalOpen}
                  onClose={() => {
                    setIsWaitingForAccountSwitch(false);
                    onSwitchAccountModalClose();
                  }}
                />
              )}
            {isLoggedIn && !isRouteWithoutDownloadTray && isAdviserOrOrg && (
              <DownloadTrayContainer />
            )}
          </>
        )}
      </React.Suspense>
    </LocalizationProvider>
  );
}

function App() {
  const user = useUser();
  const authClaims = useAuthClaims();

  useEffect(() => {
    if (configure.STAGE === 'production') {
      mixpanel.init('6c462f59e373c8adc607d57788fac511', { debug: true });
    }
  }, []);

  useEffect(() => {
    if (user?._id) {
      identifySentry(user, authClaims);
      initLogrocket(user, authClaims);
    }
  }, [user, authClaims]);

  useEffect(() => {
    initSentry();
    initLogrocket();
  }, []);

  return (
    //@ts-expect-error abc
    <ConnectedRouter history={history}>
      <Router history={history}>
        <WhiteLabellingProvider>
          <ReflagProvider publishableKey={process.env.REACT_APP_REFLAG_KEY!}>
            <AppContent />
          </ReflagProvider>
        </WhiteLabellingProvider>
      </Router>
    </ConnectedRouter>
  );
}

export default App;
